<template>
  <el-card shadow="never" v-loading="loading">
    <template slot="header">
      <div class="header">
        <span>Email History</span>
        <el-button
          size="mini"
          :disabled="loading || refreshCooldown"
          @click="manualRefresh"
        >
          Refresh
        </el-button>
      </div>
    </template>

    <el-alert
      v-if="error"
      class="error"
      type="error"
      show-icon
      :title="error"
    />

    <el-table
      :data="emails"
      stripe
      row-key="_id"
      :default-sort="{ prop: 'createdAt', order: 'descending' }"
      @expand-change="handleExpandChange"
    >
      <el-table-column type="expand">
        <template slot-scope="scope">
          <el-descriptions :column="2" size="small" border>
            <el-descriptions-item label="Created At">
              {{ scope.row.createdAt | formatDate }}
            </el-descriptions-item>
            <el-descriptions-item label="Created By">
              {{ scope.row.createdBy || '-' }}
            </el-descriptions-item>
            <el-descriptions-item v-if="isDeprecatedRecord(scope.row)" label="Record">
              <div class="deprecated-meta">
                <el-tag size="mini" type="warning" effect="plain">
                  Deprecated
                </el-tag>
                <span class="deprecated-copy">
                  {{ deprecatedMessage(scope.row) }}
                </span>
              </div>
            </el-descriptions-item>
            <el-descriptions-item label="From">
              {{ scope.row.from || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="Subject">
              {{ scope.row.subject || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="To">
              {{ formatRecipients(scope.row.to) }}
            </el-descriptions-item>
            <el-descriptions-item label="CC">
              {{ formatRecipients(scope.row.cc) }}
            </el-descriptions-item>
            <el-descriptions-item v-if="isDeprecatedRecord(scope.row)" label="Emails Included">
              <div v-if="hasEmailAccountsIncluded(scope.row)" class="detail-tags">
                <el-tag
                  v-for="account in scope.row.emailAccountsIncluded"
                  :key="account"
                  size="mini"
                  effect="plain"
                >
                  {{ account }}
                </el-tag>
              </div>
              <span v-else>-</span>
            </el-descriptions-item>
            <el-descriptions-item label="Status">
              <el-tag :type="scope.row.success ? 'success' : 'danger'">
                {{ scope.row.success ? 'Success' : 'Failed' }}
              </el-tag>
            </el-descriptions-item>
          </el-descriptions>

          <div
            v-if="scope.row.attachments && scope.row.attachments.length"
            class="detail-block"
          >
            <div class="detail-title">Attachments</div>
            <div class="detail-tags">
              <el-tag
                v-for="attachment in scope.row.attachments"
                :key="attachment.href || attachment.file"
                size="mini"
              >
                <el-link
                  v-if="attachment.href"
                  :href="attachment.href"
                  target="_blank"
                  type="primary"
                >
                  {{ attachmentLabel(attachment) }}
                </el-link>
                <span v-else>
                  {{ attachmentLabel(attachment) }}
                </span>
                <span v-if="attachment.contentType">
                  ({{ attachment.contentType }})
                </span>
              </el-tag>
            </div>
          </div>

          <el-collapse class="detail-collapse" v-model="emailDetail">
            <el-collapse-item v-if="scope.row.text" title="Raw Text" name="1">
              <pre class="detail-pre">{{ scope.row.text }}</pre>
            </el-collapse-item>
            <el-collapse-item v-if="scope.row.html" title="HTML" name="2">
              <iframe
                class="detail-iframe"
                sandbox="allow-same-origin allow-popups"
                :srcdoc="getHtmlSrcdoc(scope.row.html)"
              ></iframe>
            </el-collapse-item>
            <el-collapse-item v-if="scope.row.err" title="Error" name="3">
              <pre class="detail-pre">{{ formatJson(scope.row.err) }}</pre>
            </el-collapse-item>
            <el-collapse-item v-if="scope.row.awsResponse" title="AWS Response" name="4">
              <pre class="detail-pre">{{ formatJson(scope.row.awsResponse) }}</pre>
            </el-collapse-item>
          </el-collapse>
        </template>
      </el-table-column>

      <el-table-column label="Sent At" prop="createdAt" width="160">
        <template slot-scope="scope">
          {{ scope.row.createdAt | formatDate }}
        </template>
      </el-table-column>
      <el-table-column
        label="Subject"
        prop="subject"
        min-width="240"
        show-overflow-tooltip
      >
        <template slot-scope="scope">
          <div class="subject-cell">
            <span>{{ scope.row.subject || '-' }}</span>
            <el-tag
              v-if="isDeprecatedRecord(scope.row)"
              size="mini"
              type="warning"
              effect="plain"
            >
              Deprecated
            </el-tag>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="Source" width="120" v-if="customer">
        <template slot-scope="scope">
          <el-tag :type="scope.row.template ? 'info' : 'warning'">
            {{ scope.row.template ? 'System' : 'User' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Status" width="110">
        <template slot-scope="scope">
          <el-tag :type="scope.row.success ? 'success' : 'danger'">
            {{ scope.row.success ? 'Success' : 'Failed' }}
          </el-tag>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      v-if="total > limit"
      class="pagination"
      background
      layout="total, prev, pager, next"
      :page-size="10"
      :page-sizes="[10]"
      :current-page="page"
      :total="total"
      @current-change="onPageChange"
    />
  </el-card>
</template>

<script>
import axios from 'axios';
import { EMAIL_API_BASE } from '../env';

export default {
  name: 'EmailHistory',
  props: {
    facility: { type: String },
    customer: { type: String },
  },
  data() {
    return {
      emails: [],
      loading: false,
      error: '',
      refreshCooldown: false,
      refreshCooldownMs: 10000,
      refreshCooldownTimer: null,
      emailDetail: ['1'],
      page: 1,
      limit: 10,
      total: 0,
    };
  },
  watch: {
    async facility() {
      this.page = 1;
      await this.fetchEmails();
    },
    async customer() {
      this.page = 1;
      await this.fetchEmails();
    },
  },
  async mounted() {
    await this.fetchEmails();
  },
  beforeDestroy() {
    if (this.refreshCooldownTimer) {
      clearTimeout(this.refreshCooldownTimer);
      this.refreshCooldownTimer = null;
    }
  },
  methods: {
    getEndpoint() {
      if (this.facility && this.customer) {
        return `${EMAIL_API_BASE}/audit/${this.facility}/${this.customer}`;
      }
      if (this.facility) {
        return `${EMAIL_API_BASE}/audit/${this.facility}`;
      }
      return `${EMAIL_API_BASE}/audit`;
    },
    async fetchEmails() {
      this.loading = true;
      this.error = '';
      try {
        const response = await axios.get(this.getEndpoint(), {
          params: {
            page: this.page,
            limit: this.limit,
          },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        if (Array.isArray(response.data)) {
          this.emails = response.data;
          this.total = response.data.length;
        } else {
          this.emails = Array.isArray(response.data.emails) ? response.data.emails : [];
          this.total = Number(response.data.total) || 0;
        }
      } catch (err) {
        this.emails = [];
        this.total = 0;
        let message = 'Failed to load email history.';
        if (err && err.response && err.response.data && err.response.data.message) {
          message = err.response.data.message;
        } else if (err && err.message) {
          message = err.message;
        }
        this.error = message;
      } finally {
        this.loading = false;
      }
    },
    async onPageChange(value) {
      this.page = value;
      await this.fetchEmails();
    },
    async onPageSizeChange(value) {
      this.limit = value;
      this.page = 1;
      await this.fetchEmails();
    },
    async manualRefresh() {
      if (this.refreshCooldown) {
        return;
      }
      this.refreshCooldown = true;
      if (this.refreshCooldownTimer) {
        clearTimeout(this.refreshCooldownTimer);
      }
      this.refreshCooldownTimer = setTimeout(() => {
        this.refreshCooldown = false;
        this.refreshCooldownTimer = null;
      }, this.refreshCooldownMs);
      await this.fetchEmails();
    },
    handleExpandChange(row, expandedRows) {
      const isExpanded = Array.isArray(expandedRows)
        && expandedRows.some(expandedRow => expandedRow._id === row._id);
      if (isExpanded) {
        this.emailDetail = ['1'];
      }
    },
    formatRecipients(list) {
      if (!Array.isArray(list) || list.length === 0) {
        return '-';
      }
      return list.join(', ');
    },
    hasEmailAccountsIncluded(email) {
      return Boolean(
        email
        && Array.isArray(email.emailAccountsIncluded)
        && email.emailAccountsIncluded.length > 0,
      );
    },
    hasRecipientData(list) {
      return Array.isArray(list) && list.length > 0;
    },
    deprecatedFields(email) {
      const fields = [];
      if (!email) {
        return fields;
      }
      if (!email.subject) {
        fields.push('Subject');
      }
      if (!this.hasRecipientData(email.to)) {
        fields.push('To');
      }
      if (!this.hasRecipientData(email.cc)) {
        fields.push('CC');
      }
      return fields;
    },
    isDeprecatedRecord(email) {
      return Boolean(email && email.migrated);
    },
    formatDeprecatedFields(email) {
      return this.deprecatedFields(email).join(', ');
    },
    deprecatedMessage(email) {
      const fields = this.formatDeprecatedFields(email);
      return fields ? `Missing ${fields}` : 'Migrated legacy record';
    },
    formatJson(value) {
      if (!value) {
        return '-';
      }
      try {
        return JSON.stringify(value, null, 2);
      } catch (err) {
        return String(value);
      }
    },
    attachmentLabel(attachment) {
      return attachment.file || attachment.href || 'Attachment';
    },
    getHtmlSrcdoc(content) {
      return `<!doctype html><html><head><base target="_blank"></head><body>${content}</body></html>`;
    },
  },
};
</script>

<style scoped>
.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.error {
  margin-bottom: 12px;
}

.detail-block {
  margin-top: 12px;
}

.detail-title {
  font-weight: 600;
  margin-bottom: 6px;
}

.detail-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.detail-collapse {
  margin-top: 12px;
}

.detail-pre {
  white-space: pre-wrap;
  word-break: break-word;
  background: #f8fafc;
  padding: 8px;
  border-radius: 4px;
  font-family: "Courier New", monospace;
  font-size: 12px;
}

.detail-iframe {
  width: 100%;
  min-height: 420px;
  border: 0;
  background: #ffffff;
}

.subject-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.deprecated-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.deprecated-copy {
  color: #606266;
}

.pagination {
  margin-top: 16px;
}
</style>



// WEBPACK FOOTER //
// src/components/EmailHistory.vue